import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useSearchParams, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { DataProvider, initSDK } from '@happyspace/sdk/data';

// Initialize HappySpace SDK with business configuration
initSDK({
  businessId: '69fcf3abffe3e48dda53b728',
  apiBaseUrl: 'https://api.happyspace.one',
  taxRate: 0.0825,
});
import SEO from './components/SEO';
import Header from "./components/Header";
import Hero from "./components/Hero";
import HomeSections from "./components/HomeSections";
import HomeSearchResults from "./components/HomeSearchResults";
import Restaurants from "./components/Restaurants";
import Others from "./components/Others";
import OthersDetailPage from "./components/OthersDetailPage";
import Tailoring from "./components/Tailoring";
import AutomotiveServices from "./components/AutomotiveServices";
import AutomotiveDetailPage from "./components/AutomotiveDetailPage";
import RestaurantReview from "./components/RestaurantReview";
import RestaurantDetailPage from "./components/RestaurantDetailPage";
import TailoringDetailPage from "./components/TailoringDetailPage";
import TempleDetailPage from "./components/TempleDetailPage";
import DessertDetailPage from "./components/DessertDetailPage";
import GroceryDetailPage from "./components/GroceryDetailPage";
import VenueDetailPage from "./components/VenueDetailPage";
import HomeServiceDetailPage from "./components/HomeServiceDetailPage";
import MakeupArtistDetailPage from "./components/MakeupArtistDetailPage";
import CateringDetailPage from "./components/CateringDetailPage";
// CategoryNav removed — nav bar under hero eliminated
import ViewAllCategories from "./components/ViewAllCategories";
import AddBusiness from "./components/AddBusiness";
import AddEvent from "./components/AddEvent";
import Experiences from "./components/Experiences";
import Deals from "./components/Deals";
import Temples from "./components/Temples";
import Events from "./components/Events";
import EventDetailPage from "./components/EventDetailPage";
import Venues from "./components/Venues";
import Grocery from "./components/Grocery";
import Movies from "./components/Movies";
import Shopping from "./components/Shopping";
import ShoppingDetailPage from "./components/ShoppingDetailPage";
import CategoryHero from "./components/CategoryHero";
import Decorators from "./components/Decorators";
import EventOrganizer from "./components/EventOrganizer";
import EventProductionDetailPage from "./components/EventProductionDetailPage";
import EventManagementDetailPage from "./components/EventManagementDetailPage";
// Hookah Lounges now merged into Restaurants page
import Desserts from "./components/Desserts";
import Cafes from "./components/Cafes";
import CafeDetailPage from "./components/CafeDetailPage";
import Catering from "./components/Catering";
import Makeup from "./components/Makeup";
import MakeupHair from "./components/MakeupHair";
import SalonSpa from "./components/SalonSpa";
import SalonSpaDetailPage from "./components/SalonSpaDetailPage";
import DJs from "./components/DJs";
import DJDetailPage from "./components/DJDetailPage";
// Live Band / Singers now merged into Performing Artists page
// Food Trucks now merged into Restaurants page
import Footer from "./components/Footer";
import ContactUs from "./components/ContactUs";
import Influencers from "./components/Influencers";
import Jobs from "./components/Jobs";
import Models from "./components/Models";
import Kids from "./components/Kids";
import KidsDetailPage from "./components/KidsDetailPage";
import SeniorCitizen from "./components/SeniorCitizen";
import SeniorCitizenDetailPage from "./components/SeniorCitizenDetailPage";
import StartUps from "./components/StartUps";
import PerformingArtists from "./components/PerformingArtists";
import PerformingArtistDetailPage from "./components/PerformingArtistDetailPage";
import PhotoVideo from "./components/PhotoVideo";
import PhotoVideoDetailPage from "./components/PhotoVideoDetailPage";
import Media from "./components/Media";
import MediaDetailPage from "./components/MediaDetailPage";
import Lawyers from "./components/Lawyers";
import FinancialServices from "./components/FinancialServices";
import FinancialServiceDetailPage from "./components/FinancialServiceDetailPage";
import Insurance from "./components/Insurance";
import Consulting from "./components/Consulting";
import Realtors from "./components/Realtors";
import DatingMatrimonial from "./components/DatingMatrimonial";
import NonProfitOrg from "./components/NonProfitOrg";
import NonProfitDetailPage from "./components/NonProfitDetailPage";
import Youth from "./components/Youth";
import Women from "./components/Women";
import Cricket from "./components/Cricket";
import HomeServices from "./components/HomeServices";
import HealthWellness from "./components/HealthWellness";
import HealthWellnessDetailPage from "./components/HealthWellnessDetailPage";
import TravelAgents from "./components/TravelAgents";
import TravelAgentDetailPage from "./components/TravelAgentDetailPage";
import NewsTicker from "./components/NewsTicker";
import ThisWeeksScoop from "./components/ThisWeeksScoop";
import DallasWeeklyScoop from "./components/DallasWeeklyScoop";
import EcommerceForDesiBusinesses from "./pages/blog/ecommerce-for-dallas-desi-businesses";
import HowToStartEcommerceBusiness from "./pages/blog/how-to-start-ecommerce-business";
import BestIndianRestaurantsDFW from "./pages/blog/best-indian-restaurants-dfw";
import BestIndianGroceryStoresDFW from "./pages/blog/best-indian-grocery-stores-dfw";
import UpcomingDesiEventsDFW from "./pages/blog/upcoming-desi-events-dfw";
import BlogIndex from "./pages/blog/BlogIndex";
import AdminLogin from "./components/AdminLogin";
import Admin from "./components/Admin";
import { AdminProvider } from "./context/AdminContext";
import AdminBanner from "./admin/AdminBanner";
import OurStory from "./components/OurStory";
import { useManagedCollection } from "./admin/useManagedCollection";
import { AdminReorder } from "./admin/AdminControls";

function HomePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get("q") || "");
  const [submittedQuery, setSubmittedQuery] = useState<string | null>(() => {
    const initial = searchParams.get("q");
    return initial && initial.trim().length > 0 ? initial.trim() : null;
  });

  const runSearch = (overrideQuery?: string) => {
    const q = (overrideQuery ?? searchQuery).trim();
    setSubmittedQuery(q.length > 0 ? q : null);
  };

  // Category-page heroes (Cricket, Catering, DJs, etc.) redirect here with
  // ?q=<term> to run a real site-wide search instead of only filtering their
  // own local list. Pick up that param whenever it changes.
  useEffect(() => {
    const q = searchParams.get("q");
    const trimmed = q ? q.trim() : "";
    if (trimmed.length > 0) {
      setSearchQuery(trimmed);
      setSubmittedQuery(trimmed);
    }
  }, [searchParams]);

  // datePublished: the site's real original launch date (5/28/2026, per
  // project records). dateModified: the last date the homepage's actual
  // content genuinely changed (e.g. copy/section edits) — update this by
  // hand when that happens, never auto-bind it to build time or "today".
  const homeJsonLd = [
    {
      "@type": "WebPage",
      "@id": "https://dallasdesiscoop.com/#webpage",
      "url": "https://dallasdesiscoop.com/",
      "name": "Dallas Desi Scoop | Indian Restaurants, Events & Businesses in DFW",
      "isPartOf": { "@id": "https://dallasdesiscoop.com/#website" },
      "about": { "@id": "https://dallasdesiscoop.com/#organization" },
      "datePublished": "2026-05-28",
      "dateModified": "2026-08-29",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://dallasdesiscoop.com/" }
        ]
      }
    }
  ];

  // Admin-reorderable homepage sections (order persists + reflects publicly).
  // NewsTicker is intentionally NOT part of this list — like every other page,
  // it's fixed directly below the hero and can't be reordered or hidden.
  const SECTIONS = [
    { key: "hero", label: "Hero / Search" },
    { key: "main", label: "Main Content" },
  ];
  const secMc = useManagedCollection<{ key: string; label: string }>(
    "home_sections",
    SECTIONS,
    (s) => s.key
  );

  const renderSection = (key: string) => {
    if (key === "hero")
      return <Hero searchQuery={searchQuery} setSearchQuery={setSearchQuery} onSearch={runSearch} />;
    if (key === "main")
      return submittedQuery ? (
        <HomeSearchResults
          query={submittedQuery}
          location="All Locations"
          onClear={() => { setSubmittedQuery(null); setSearchQuery(""); setSearchParams({}); }}
        />
      ) : (
        <HomeSections />
      );
    return null;
  };

  return (
    <>
      <SEO
        title="Dallas Desi Scoop | Indian Restaurants, Events & Businesses in DFW"
        description="Dallas Desi Scoop is the local guide to Indian, Pakistani and Desi restaurants, events, concerts, grocery stores, temples and businesses across Dallas–Fort Worth."
        canonical="/"
        jsonLd={homeJsonLd}
      />
      {secMc.items.map((s) => (
        <AdminReorder key={s._key} mc={secMc} item={s}>
          {renderSection(s.key)}
          {/* Ticker sits fixed directly below the hero, same as every other page — not reorderable/hideable, regardless of any admin drag-reorder of sections */}
          {s.key === "hero" && <NewsTicker />}
        </AdminReorder>
      ))}
    </>
  );
}

function VenuesSlugRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/party-venues/${slug ?? ""}`} replace />;
}

// Several guides were generated more than once under different slugs. Search
// engines index one of a duplicate set at most and park the rest as
// "discovered, not indexed", so one canonical guide per topic keeps its fixed
// route and every retired slug redirects to it. Unknown slugs go to /blog.
const RETIRED_BLOG_SLUGS: Record<string, string> = {
  "best-indian-restaurants-dfw-guide": "/blog/best-indian-restaurants-dfw",
  "best-indian-restaurants-in-dfw-complete-guide": "/blog/best-indian-restaurants-dfw",
  "best-indian-grocery-stores-dfw-guide": "/blog/best-indian-grocery-stores-dfw",
  "indian-grocery-stores-dallas": "/blog/best-indian-grocery-stores-dfw",
  "upcoming-desi-events-dfw-concerts-festivals": "/blog/upcoming-desi-events-dfw",
  "upcoming-indian-events-dfw": "/blog/upcoming-desi-events-dfw",
  "ecommerce-business-for-sale-guide": "/blog/how-to-start-ecommerce-business",
  "what-is-ecommerce-meaning-definition-desi-businesses": "/blog/ecommerce-for-dallas-desi-businesses",
};

function BlogSlugRedirect() {
  const { slug } = useParams();
  return <Navigate to={RETIRED_BLOG_SLUGS[slug ?? ""] ?? "/blog"} replace />;
}

function ComingSoonPage({ title, backgroundImage }: { title: string; backgroundImage?: string }) {
  return (
    <div className="min-h-screen">
      <CategoryHero title={title} placeholder={`Search ${title}…`} backgroundImage={backgroundImage} />
      <NewsTicker />
      <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 py-16 text-center">
        <p className="text-gray-500 text-[15px]" style={{ fontFamily: "'Poppins', sans-serif" }}>
          {title} listings coming soon.
        </p>
      </div>
    </div>
  );
}

// AppShell is the router-agnostic tree: everything except the top-level
// Router + HelmetProvider wrappers. The CLIENT entry (App, below) wraps it in
// BrowserRouter for the browser. The SERVER entry (src/entry-server.tsx)
// wraps the exact same tree in StaticRouter for build-time prerendering —
// so the prerendered HTML and the hydrated client tree are structurally
// identical, which is what makes hydration work without a mismatch.
export function AppShell() {
  useEffect(() => {
    if (typeof window !== "undefined") window.scrollTo(0, 0);
  }, []);

  return (
    <AdminProvider>
      <DataProvider>
        <div className="min-h-screen">
          <AdminBanner />
          <Header />
          <main>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/our-story" element={<OurStory />} />

              {/* ── Core pages ── */}
              <Route path="/restaurants" element={<Restaurants />} />

              <Route path="/restaurants/:slug/review" element={<RestaurantReview />} />
              <Route path="/restaurants/:slug" element={<RestaurantDetailPage />} />

              <Route path="/events" element={
                <>
                  <SEO
                    title="Indian Events in Dallas–Fort Worth | Dallas Desi Scoop"
                    description="Upcoming Indian community events, cultural festivals, expos, and concerts across Dallas–Fort Worth. Find South Asian events in Frisco, Plano, Garland, Allen & more."
                    canonical="/events"
                    jsonLd={[{
                      "@type": "BreadcrumbList",
                      "itemListElement": [
                        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://dallasdesiscoop.com/" },
                        { "@type": "ListItem", "position": 2, "name": "Events", "item": "https://dallasdesiscoop.com/events" }
                      ]
                    }]}
                  />
                  <Events />
                </>
              } />

              <Route path="/events/:slug" element={<EventDetailPage />} />

              <Route path="/party-venues" element={
                <>
                  <SEO
                    title="Party Venues & Event Venues in Dallas–Fort Worth | Dallas Desi Scoop"
                    description="Find party venues and event venues in Dallas–Fort Worth: banquet halls, wedding venues, party halls and event spaces for weddings, receptions, birthdays, engagements and Desi celebrations in Frisco, Plano, Irving, Dallas, McKinney, Carrollton & across DFW."
                    canonical="/party-venues"
                    jsonLd={[{
                      "@type": "BreadcrumbList",
                      "itemListElement": [
                        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://dallasdesiscoop.com/" },
                        { "@type": "ListItem", "position": 2, "name": "Party Venues", "item": "https://dallasdesiscoop.com/party-venues" }
                      ]
                    }]}
                  />
                  <Venues />
                </>
              } />

              <Route path="/party-venues/:slug" element={<VenueDetailPage />} />
              <Route path="/venues" element={<Navigate to="/party-venues" replace />} />
              <Route path="/venues/:slug" element={<VenuesSlugRedirect />} />

              <Route path="/event-management" element={
                <>
                  <SEO
                    title="Event Management in Dallas–Fort Worth | Dallas Desi Scoop"
                    description="Find top Indian wedding and event decorators in DFW. Browse mandap decorators, floral designers, and full-service event decorators in Frisco, Plano, Irving & more."
                    canonical="/event-management"
                    jsonLd={[{
                      "@type": "BreadcrumbList",
                      "itemListElement": [
                        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://dallasdesiscoop.com/" },
                        { "@type": "ListItem", "position": 2, "name": "Event Management", "item": "https://dallasdesiscoop.com/event-management" }
                      ]
                    }]}
                  />
                  <Decorators />
                </>
              } />

              <Route path="/event-management/:slug" element={<EventManagementDetailPage />} />
              <Route path="/event-production/:slug" element={<EventProductionDetailPage />} />

              {/* Legacy URL redirect: /decorators -> /event-management */}
              <Route path="/decorators" element={<Navigate to="/event-management" replace />} />

              <Route path="/event-production" element={
                <>
                  <SEO
                    title="Indian Event Production Companies in DFW"
                    description="Discover Indian event production, marketing, and management companies serving Dallas–Fort Worth. From Bollywood concerts to cultural festivals, find the right team for your event."
                    canonical="/event-production"
                  />
                  <EventOrganizer />
                </>
              } />

              <Route path="/concerts" element={
                <>
                  <SEO
                    title="Indian Concerts & Events in Dallas–Fort Worth | Dallas Desi Scoop"
                    description="Upcoming Indian concerts, Bollywood shows, and desi events in DFW. Buy tickets for Vishal-Shekhar, Ammy Virk, and more live shows in Garland, Allen, Plano & Dallas."
                    canonical="/concerts"
                    ogType="website"
                  />
                  <Events title="Concerts/Events" />
                </>
              } />

              <Route path="/temples" element={
                <>
                  <SEO
                    title="Hindu Temples in Dallas & DFW | Find a Temple Near You"
                    description="Find Hindu temples in Dallas, TX and across DFW: addresses, phone numbers, and directions for 19+ temples in Frisco, Irving, Plano, Carrollton, Allen and more."
                    canonical="/temples"
                    jsonLd={[{
                      "@type": "BreadcrumbList",
                      "itemListElement": [
                        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://dallasdesiscoop.com/" },
                        { "@type": "ListItem", "position": 2, "name": "Temples/Spiritual Services", "item": "https://dallasdesiscoop.com/temples" }
                      ]
                    }]}
                  />
                  <Temples />
                </>
              } />

              <Route path="/temples/:slug" element={<TempleDetailPage />} />

              <Route path="/dessert-shops" element={
                <>
                  <SEO
                    title="Dessert Shops in Dallas-Fort Worth | Indian Mithai & Bakeries"
                    description="Browse the best dessert shops in DFW: Indian mithai, gulab jamun, jalebi, rasmalai, cakes, and ice cream in Frisco, Plano, Irving, Dallas & beyond."
                    canonical="/dessert-shops"
                  />
                  <Desserts />
                </>
              } />

              <Route path="/dessert-shops/:slug" element={<DessertDetailPage />} />

              {/* Legacy URL redirects: /sweet-temptations and /desserts -> /dessert-shops */}
              <Route path="/sweet-temptations" element={<Navigate to="/dessert-shops" replace />} />
              <Route path="/desserts" element={<Navigate to="/dessert-shops" replace />} />

              <Route path="/cafe" element={
                <>
                  <SEO
                    title="Desi Cafes in Dallas–Fort Worth | Chai, Coffee & Snack Spots | Dallas Desi Scoop"
                    description="Browse the best Indian and South Asian cafes in DFW: chai cafes, filter kaapi, Irani cafes, fusion dessert cafes, and snack spots in Frisco, Plano, Irving, Dallas & beyond."
                    canonical="/cafe"
                  />
                  <Cafes />
                </>
              } />

              <Route path="/cafe/:slug" element={<CafeDetailPage />} />

              <Route path="/catering" element={
                <>
                  <SEO
                    title="Indian Catering Services in Dallas–Fort Worth | Dallas Desi Scoop"
                    description="Find top Indian and South Asian catering services in DFW. Browse caterers for weddings, parties, and events in Frisco, Plano, Irving, Dallas & more."
                    canonical="/catering"
                  />
                  <Catering />
                </>
              } />

              <Route path="/movies" element={
                <>
                  <SEO
                    title="Bollywood Movies in Dallas–Fort Worth | Dallas Desi Scoop"
                    description="Find Bollywood and South Indian movies playing in Dallas–Fort Worth theatres. Upcoming Hindi, Tamil, Telugu, and Malayalam film screenings in DFW."
                    canonical="/movies"
                  />
                  <Movies />
                </>
              } />

              <Route path="/grocery" element={
                <>
                  <SEO
                    title="Indian Grocery Stores in Dallas–Fort Worth | Dallas Desi Scoop"
                    description="Find Indian, Pakistani and halal grocery stores across Dallas–Fort Worth: fresh produce, spices, rice, lentils and sweets in Dallas, Frisco, Plano, Irving and more."
                    canonical="/grocery"
                    jsonLd={[{
                      "@type": "BreadcrumbList",
                      "itemListElement": [
                        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://dallasdesiscoop.com/" },
                        { "@type": "ListItem", "position": 2, "name": "Indian Grocery", "item": "https://dallasdesiscoop.com/grocery" }
                      ]
                    }]}
                  />
                  <Grocery />
                </>
              } />

              <Route path="/grocery/:slug" element={<GroceryDetailPage />} />

              <Route path="/shopping" element={
                <>
                  <SEO
                    title="Indian Clothing, Gold & Gifts in Dallas | Shop Local"
                    description="Discover Indian clothing boutiques, jewelry stores, gold shops, and desi shopping destinations across Dallas–Fort Worth. Browse traditional and modern South Asian apparel, accessories, and gifts in Frisco, Plano, Irving & more."
                    canonical="/shopping"
                  />
                  <Shopping />
                </>
              } />
              <Route path="/shopping/:slug" element={<ShoppingDetailPage />} />

              <Route path="/lawyers" element={
                <>
                  <SEO
                    title="Indian Immigration & Business Lawyers in Dallas–Fort Worth | Dallas Desi Scoop"
                    description="Find experienced Indian immigration attorneys and business lawyers in DFW. Browse South Asian legal professionals serving Frisco, Plano, Irving and Dallas."
                    canonical="/lawyers"
                  />
                  <Lawyers />
                </>
              } />

              <Route path="/realtors" element={
                <>
                  <SEO
                    title="Indian Realtors & Real Estate Agents in Dallas–Fort Worth | Dallas Desi Scoop"
                    description="Find trusted Indian realtors and real estate agents in DFW. Browse South Asian property professionals helping buyers and sellers in Frisco, Plano, McKinney, Irving & more."
                    canonical="/realtors"
                  />
                  <Realtors />
                </>
              } />

              <Route path="/dating-matrimonial" element={
                <>
                  <SEO
                    title="Indian Matrimonial & Dating Services in Dallas–Fort Worth | Dallas Desi Scoop"
                    description="Find Indian matrimonial services, desi dating platforms, and matchmakers serving the South Asian community in Dallas–Fort Worth."
                    canonical="/dating-matrimonial"
                  />
                  <DatingMatrimonial />
                </>
              } />

              <Route path="/non-profit-org" element={
                <>
                  <SEO
                    title="Indian & South Asian Non-Profit Organizations in DFW | Dallas Desi Scoop"
                    description="Discover South Asian non-profit organizations, charities, and community groups in Dallas–Fort Worth. Find volunteer opportunities and ways to give back to the desi community."
                    canonical="/non-profit-org"
                  />
                  <NonProfitOrg />
                </>
              } />
              <Route path="/non-profit-org/:slug" element={<NonProfitDetailPage />} />

              <Route path="/youth" element={
                <>
                  <SEO
                    title="South Asian Youth Programs & Activities in DFW | Dallas Desi Scoop"
                    description="Find Indian and South Asian youth programs, cultural classes, and after-school activities in Dallas–Fort Worth. Classical dance, music, arts, and more."
                    canonical="/youth"
                  />
                  <Youth />
                </>
              } />

              <Route path="/women" element={
                <>
                  <SEO
                    title="South Asian Women's Groups & Services in DFW | Dallas Desi Scoop"
                    description="Discover Indian and South Asian women's organizations, networking groups, and services in Dallas–Fort Worth. Empowering desi women across the DFW community."
                    canonical="/women"
                  />
                  <Women />
                </>
              } />

              <Route path="/cricket" element={
                <>
                  <SEO
                    title="Cricket Leagues & Clubs in Dallas–Fort Worth | Dallas Desi Scoop"
                    description="Find cricket leagues, clubs, and registrations for the South Asian community in Dallas–Fort Worth, including the Empower Play Project women's indoor cricket league."
                    canonical="/cricket"
                  />
                  <Cricket />
                </>
              } />

              <Route path="/this-weeks-scoop" element={
                <>
                  <SEO
                    title="This Week's Scoop — DFW Desi News & Highlights | Dallas Desi Scoop"
                    description="This week's top stories, events, and highlights from the South Asian community in Dallas–Fort Worth. Stay up to date with the latest desi news in DFW."
                    canonical="/this-weeks-scoop"
                  />
                  <ThisWeeksScoop />
                </>
              } />

              <Route path="/dallas-monthly-scoop" element={
                <>
                  <SEO
                    title="Dallas Monthly Scoop — Community News | Dallas Desi Scoop"
                    description="Your monthly roundup of Indian and South Asian community news, events, business spotlights and stories from across Dallas–Fort Worth."
                    canonical="/dallas-monthly-scoop"
                  />
                  <DallasWeeklyScoop />
                </>
              } />

              {/* 301-style redirect: preserve SEO value and existing links to the old slug */}
              <Route path="/dallas-weekly-scoop" element={<Navigate to="/dallas-monthly-scoop" replace />} />

              <Route path="/health-wellness" element={
                <>
                  <SEO
                    title="Indian Health & Wellness Services in DFW"
                    description="Find Indian and South Asian health professionals, wellness centres, Ayurveda practitioners, and holistic health services across DFW."
                    canonical="/health-wellness"
                  />
                  <HealthWellness />
                </>
              } />

              <Route path="/health-wellness/:slug" element={<HealthWellnessDetailPage />} />

              <Route path="/influencers" element={
                <>
                  <SEO
                    title="South Asian Influencers & Content Creators in Dallas–Fort Worth | Dallas Desi Scoop"
                    description="Discover Indian and South Asian social media influencers, food bloggers, and content creators based in Dallas–Fort Worth. Collaborate with DFW's top desi creators."
                    canonical="/influencers"
                  />
                  <Influencers />
                </>
              } />

              <Route path="/jobs" element={
                <>
                  <SEO
                    title="Desi Jobs in Dallas–Fort Worth | Post a Job & Find Local Work | Dallas Desi Scoop"
                    description="Browse job openings from Indian and South Asian businesses in Dallas–Fort Worth, or post a job to reach the local Desi community. Full-time, part-time, contract, and internship roles."
                    canonical="/jobs"
                  />
                  <Jobs />
                </>
              } />

              <Route path="/models" element={
                <>
                  <SEO
                    title="South Asian Models & Talent in Dallas–Fort Worth | Dallas Desi Scoop"
                    description="Discover Indian and South Asian models and creative talent based in Dallas–Fort Worth. Collaborate with DFW's top desi models for brand and event campaigns."
                    canonical="/models"
                  />
                  <Models />
                </>
              } />

              <Route path="/categories" element={
                <>
                  <SEO
                    title="All Categories — South Asian Businesses in DFW | Dallas Desi Scoop"
                    description="Browse all categories of South Asian businesses in Dallas–Fort Worth: restaurants, temples, grocery, events, DJs, decorators, lawyers, realtors, and much more."
                    canonical="/categories"
                  />
                  <ViewAllCategories />
                </>
              } />

              <Route path="/add-event" element={
                <>
                  <SEO
                    title="Add Your Indian Event — List on Dallas Desi Scoop Free"
                    description="Submit your upcoming Indian or South Asian event to Dallas Desi Scoop for free. Reach thousands of DFW community members looking for desi concerts, festivals, expos and cultural events."
                    canonical="/add-event"
                    noindex={false}
                  />
                  <AddEvent />
                </>
              } />

              <Route path="/add-business" element={
                <>
                  <SEO
                    title="List Your Desi Business Free — Add to Dallas Desi Scoop Directory"
                    description="Add your Indian or South Asian business to the Dallas Desi Scoop directory for free. Reach thousands of DFW community members looking for restaurants, events, services & more."
                    canonical="/add-business"
                  />
                  <AddBusiness />
                </>
              } />

              <Route path="/contact" element={
                <>
                  <SEO
                    title="Contact Dallas Desi Scoop — Get in Touch"
                    description="Contact the Dallas Desi Scoop team. Reach out for advertising, partnerships, event listings, business submissions, or general enquiries about the DFW South Asian community guide."
                    canonical="/contact"
                  />
                  <ContactUs />
                </>
              } />

              <Route path="/makeup" element={
                <>
                  <SEO
                    title="Indian Bridal Makeup Artists in Dallas–Fort Worth | Dallas Desi Scoop"
                    description="Find expert Indian bridal makeup artists and beauty professionals in DFW. Browse South Asian bridal makeup, mehndi, and beauty services in Frisco, Plano, Irving & more."
                    canonical="/makeup"
                  />
                  <Makeup />
                </>
              } />

              <Route path="/experiences" element={
                <>
                  <SEO
                    title="Unique South Asian Experiences in Dallas–Fort Worth | Dallas Desi Scoop"
                    description="Discover unique South Asian experiences in DFW — from Indian cooking classes and cultural tours to mehndi workshops and Bollywood dance lessons."
                    canonical="/experiences"
                  />
                  <Experiences />
                </>
              } />

              <Route path="/deals" element={
                <>
                  <SEO
                    title="Desi Deals & Offers in Dallas–Fort Worth | Dallas Desi Scoop"
                    description="Find the best deals, discounts, and special offers from Indian restaurants, businesses, and services in Dallas–Fort Worth. Save money while supporting the desi community."
                    canonical="/deals"
                  />
                  <Deals />
                </>
              } />

              <Route path="/BeautyServices" element={
                <>
                  <SEO
                    title="Indian Bridal Makeup & Hair Stylists in DFW | Dallas Desi Scoop"
                    description="Find Indian bridal makeup artists and hair stylists in Dallas–Fort Worth: airbrush makeup, henna, draping and mehndi across DFW."
                    canonical="/BeautyServices"
                  />
                  <MakeupHair />
                </>
              } />
              <Route path="/BeautyServices/:slug" element={<MakeupArtistDetailPage />} />

              <Route path="/makeup-hair" element={<Navigate to="/BeautyServices" replace />} />

              <Route path="/salon-spa" element={
                <>
                  <SEO
                    title="Indian Salons & Spas in Dallas–Fort Worth | Dallas Desi Scoop"
                    description="Discover Indian salons, desi spas, and South Asian beauty centres in DFW. Threading, facials, massages, waxing, and more in Frisco, Plano, Irving, Carrollton & beyond."
                    canonical="/salon-spa"
                  />
                  <SalonSpa />
                </>
              } />

              <Route path="/salon-spa/:slug" element={<SalonSpaDetailPage />} />

              <Route path="/djs" element={
                <>
                  <SEO
                    title="Indian Wedding DJs in Dallas–Fort Worth | Dallas Desi Scoop"
                    description="Find top Indian wedding DJs and Bollywood DJs in DFW. Browse South Asian DJs specialising in Punjabi, Hindi, Tamil, and Telugu music for weddings and events in DFW."
                    canonical="/djs"
                  />
                  <DJs />
                </>
              } />

              <Route path="/djs/:slug" element={<DJDetailPage />} />

              <Route path="/kids" element={
                <>
                  <SEO
                    title="South Asian Kids Activities & Classes in DFW | Dallas Desi Scoop"
                    description="Find Indian kids' activities, Bharatanatyam dance classes, Carnatic music lessons, Hindi language schools, and South Asian cultural programs in Dallas–Fort Worth."
                    canonical="/kids"
                  />
                  <Kids />
                </>
              } />

              <Route path="/kids/:slug" element={<KidsDetailPage />} />

              <Route path="/senior-citizen" element={
                <>
                  <SEO
                    title="South Asian Senior Services & Groups in Dallas–Fort Worth | Dallas Desi Scoop"
                    description="Find Indian senior citizen groups, community centres, and services for South Asian seniors in Dallas–Fort Worth. Social clubs, health support, and cultural programs for desi elders in DFW."
                    canonical="/senior-citizen"
                  />
                  <SeniorCitizen />
                </>
              } />

              <Route path="/senior-citizen/:slug" element={<SeniorCitizenDetailPage />} />

              <Route path="/start-ups" element={
                <>
                  <SEO
                    title="South Asian Startups & Entrepreneurs in Dallas–Fort Worth | Dallas Desi Scoop"
                    description="Discover Indian and South Asian startups, tech entrepreneurs, and desi-founded businesses in Dallas–Fort Worth. Connect with the DFW desi entrepreneurship community."
                    canonical="/start-ups"
                  />
                  <StartUps />
                </>
              } />

              <Route path="/others" element={
                <>
                  <SEO
                    title="Other South Asian Businesses & Services in DFW | Dallas Desi Scoop"
                    description="Find other Indian and South Asian businesses and services in Dallas–Fort Worth not listed in our main categories. Browse the full DFW desi business directory."
                    canonical="/others"
                  />
                  <Others />
                </>
              } />

              <Route path="/others/:slug" element={<OthersDetailPage />} />

              <Route path="/tailoring" element={
                <>
                  <SEO
                    title="South Asian Tailors & Boutiques in Dallas–Fort Worth | Dallas Desi Scoop"
                    description="Find Indian and South Asian tailors, boutiques, and alteration services in Dallas–Fort Worth for blouses, sarees, sherwanis, and custom desi outfits."
                    canonical="/tailoring"
                  />
                  <Tailoring />
                </>
              } />

              <Route path="/tailoring/:slug" element={<TailoringDetailPage />} />

              <Route path="/automotive-services" element={
                <>
                  <SEO
                    title="Automotive Services in Dallas–Fort Worth | Dallas Desi Scoop"
                    description="Find South Asian-owned auto repair shops, detailing, tire, and body shop services across Dallas–Fort Worth."
                    canonical="/automotive-services"
                  />
                  <AutomotiveServices />
                </>
              } />
              <Route path="/automotive-services/:slug" element={<AutomotiveDetailPage />} />

              <Route path="/bartenders" element={
                <>
                  <SEO
                    title="Indian Event Bartenders in Dallas–Fort Worth | Dallas Desi Scoop"
                    description="Find professional bartenders for Indian weddings and South Asian events in DFW. Cocktail and mocktail specialists experienced with desi celebrations."
                    canonical="/bartenders"
                  />
                  <ComingSoonPage title="Bartenders" />
                </>
              } />

              <Route path="/bakers" element={
                <>
                  <SEO
                    title="Indian Bakers & Cake Artists in Dallas–Fort Worth | Dallas Desi Scoop"
                    description="Find Indian bakers specialising in wedding cakes, mithai-inspired cakes, and custom desi celebration cakes in Dallas–Fort Worth."
                    canonical="/bakers"
                  />
                  <ComingSoonPage title="Bakers" />
                </>
              } />

              <Route path="/catering-only" element={
                <>
                  <SEO
                    title="Indian Caterers for Weddings & Events in DFW | Dallas Desi Scoop"
                    description="Indian catering companies in DFW for weddings, corporate events and parties. North Indian, South Indian, vegetarian and fusion menus."
                    canonical="/catering-only"
                  />
                  <Catering />
                </>
              } />

              <Route path="/catering-only/:slug" element={<CateringDetailPage />} />

              <Route path="/home-services" element={
                <>
                  <SEO
                    title="South Asian Home Services in Dallas–Fort Worth | Dallas Desi Scoop"
                    description="Find Indian and South Asian home service professionals in DFW — plumbers, electricians, cleaners, interior designers, and more serving the desi community."
                    canonical="/home-services"
                  />
                  <HomeServices />
                </>
              } />
              <Route path="/home-services/:slug" element={<HomeServiceDetailPage />} />

              <Route path="/karaoke-spots" element={
                <>
                  <SEO
                    title="South Asian Karaoke Spots in Dallas–Fort Worth | Dallas Desi Scoop"
                    description="Find Indian and South Asian karaoke spots, lounges, and bars in Dallas–Fort Worth for Bollywood and desi sing-alongs."
                    canonical="/karaoke-spots"
                  />
                  <ComingSoonPage title="Karaoke Spots" />
                </>
              } />

              <Route path="/it-consulting" element={
                <>
                  <SEO
                    title="South Asian IT Consultants in Dallas–Fort Worth | Dallas Desi Scoop"
                    description="Find Indian and South Asian IT consulting professionals, software firms, and tech consultants serving the desi community in Dallas–Fort Worth."
                    canonical="/it-consulting"
                  />
                  <Consulting />
                </>
              } />

              <Route path="/financial-services" element={
                <>
                  <SEO
                    title="Indian Financial Services in Dallas–Fort Worth | Dallas Desi Scoop"
                    description="Find South Asian financial advisors, tax preparers, accountants, and insurance agents serving the community in Dallas–Fort Worth."
                    canonical="/financial-services"
                  />
                  <FinancialServices />
                </>
              } />

              <Route path="/financial-services/:slug" element={<FinancialServiceDetailPage />} />

              <Route path="/insurance" element={
                <>
                  <SEO
                    title="Indian Insurance Agents in Dallas–Fort Worth | Dallas Desi Scoop"
                    description="Find South Asian insurance agents and agencies in DFW — auto, home, life, and health insurance coverage."
                    canonical="/insurance"
                  />
                  <Insurance />
                </>
              } />

              <Route path="/travel-agents" element={
                <>
                  <SEO
                    title="Indian Travel Agents in Dallas–Fort Worth | Dallas Desi Scoop"
                    description="Find Indian travel agents and desi travel specialists in DFW. Plan trips to India, South Asia, and beyond with travel professionals who understand South Asian travel needs."
                    canonical="/travel-agents"
                  />
                  <TravelAgents />
                </>
              } />

              <Route path="/travel-agents/:slug" element={<TravelAgentDetailPage />} />

              <Route path="/performing-artists" element={
                <>
                  <SEO
                    title="Indian Singers, DJs & Performers in Dallas"
                    description="Find Indian classical dancers, Bollywood singers, live bands, DJs, and South Asian entertainers in Dallas–Fort Worth. Book desi performing artists for weddings, concerts, events & celebrations."
                    canonical="/performing-artists"
                  />
                  <PerformingArtists />
                </>
              } />

              <Route path="/performing-artists/:slug" element={<PerformingArtistDetailPage />} />

              <Route path="/media" element={
                <>
                  <SEO
                    title="South Asian Media & Publications in Dallas–Fort Worth | Dallas Desi Scoop"
                    description="Discover Indian media companies, South Asian publications, radio stations, and online media serving the desi community in Dallas–Fort Worth."
                    canonical="/media"
                  />
                  <Media />
                </>
              } />

              <Route path="/media/:slug" element={<MediaDetailPage />} />

              <Route path="/photo-video" element={
                <>
                  <SEO
                    title="Indian Wedding Photographers & Videographers in DFW | Dallas Desi Scoop"
                    description="Find top Indian wedding photographers and videographers in Dallas–Fort Worth. South Asian wedding photography specialists for traditional ceremonies, receptions, and pre-wedding shoots."
                    canonical="/photo-video"
                    jsonLd={[{
                      "@type": "BreadcrumbList",
                      "itemListElement": [
                        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://dallasdesiscoop.com/" },
                        { "@type": "ListItem", "position": 2, "name": "Photo & Video", "item": "https://dallasdesiscoop.com/photo-video" }
                      ]
                    }]}
                  />
                  <PhotoVideo />
                </>
              } />

              <Route path="/photo-video/:slug" element={<PhotoVideoDetailPage />} />

              {/* ── Admin ── */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<Admin />} />

              {/* ── Blog ── */}
              <Route path="/blog" element={<BlogIndex />} />
              <Route path="/blog/ecommerce-for-dallas-desi-businesses" element={<EcommerceForDesiBusinesses />} />
              <Route path="/blog/how-to-start-ecommerce-business" element={<HowToStartEcommerceBusiness />} />
              <Route path="/blog/best-indian-restaurants-dfw" element={<BestIndianRestaurantsDFW />} />
              <Route path="/blog/best-indian-grocery-stores-dfw" element={<BestIndianGroceryStoresDFW />} />
              <Route path="/blog/upcoming-desi-events-dfw" element={<UpcomingDesiEventsDFW />} />
              {/* Retired post slugs land here and go to the guide that replaced them.
                  Parameterised on purpose: a fixed route would put the old URL back
                  in the sitemap as a duplicate. */}
              <Route path="/blog/:slug" element={<BlogSlugRedirect />} />
            </Routes>
          </main>
          <Footer />
        </div>
      </DataProvider>
    </AdminProvider>
  );
}

// CLIENT entry point: wraps AppShell in BrowserRouter + HelmetProvider for
// the browser. This is the default export main.tsx mounts with ReactDOM.
export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
    </HelmetProvider>
  );
}


/* injected: entrance-visibility failsafe — content must never stay invisible */
if (typeof window !== 'undefined') {
  var __hsReveal = function(){
    try {
      document.querySelectorAll('.reveal,.fade-in,.fade-up,[data-reveal],[data-aos]').forEach(function(el){ el.classList.add('in','visible','revealed','active','aos-animate'); });
      document.querySelectorAll('*').forEach(function(el){
        var htmlEl = el as HTMLElement;
        var cs = getComputedStyle(htmlEl);
        if (parseFloat(cs.opacity) < 0.05 && htmlEl.getClientRects().length) {
          htmlEl.style.setProperty('opacity','1','important');
          if (cs.transform && cs.transform !== 'none') htmlEl.style.setProperty('transform','none','important');
        }
      });
    } catch(e){}
  };
  window.addEventListener('load', function(){ setTimeout(__hsReveal, 1500); setTimeout(__hsReveal, 3500); });
}
